/**
 * iter57 — Dialog pour importer les lignes d'un devis (autonome) dans un OR.
 *
 * Use case : le devis a été envoyé au client, il l'accepte → on récupère
 * les lignes (MO, articles, forfait) dans l'OR ouvert en REMPLAÇANT les
 * lignes actuelles (souvent vides ou brouillon).
 *
 * Le devis lui-même reste intact côté commercial.
 */
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Loader2, FileText, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const STATUT_LABELS = {
  en_suspens: "En suspens",
  envoye: "Envoyé",
  accepte: "Accepté",
  refuse: "Refusé",
  planifie: "Planifié",
  converti: "Converti",
  expire: "Expiré",
};

function fmtCHF(n) {
  if (n == null) return "—";
  return (n || 0).toLocaleString("fr-CH", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export default function ImportDevisDialog({ open, onClose, orId, clientId, vehiculeId, currentLignesCount, onImported }) {
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [importing, setImporting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSelectedId(null);
    setLoading(true);
    // On vise les devis du même client (et idéalement du même véhicule),
    // exclure ceux déjà refusés / convertis.
    const params = { client_id: clientId, limit: 50 };
    api.get("/devis-all", { params })
      .then(({ data }) => {
        let list = (data.items || data || []).filter(
          (d) => !["refuse", "converti"].includes(d.cycle || d.statut),
        );
        // Préférer ceux du même véhicule, puis le reste.
        if (vehiculeId) {
          list = list.sort((a, b) => {
            const av = a.vehicule_id === vehiculeId ? 0 : 1;
            const bv = b.vehicule_id === vehiculeId ? 0 : 1;
            return av - bv;
          });
        }
        setItems(list);
      })
      .catch(() => toast.error("Erreur de chargement des devis"))
      .finally(() => setLoading(false));
  }, [open, clientId, vehiculeId]);

  const handleImport = async () => {
    if (!selectedId) return;
    if (currentLignesCount > 0) {
      const ok = window.confirm(
        `Cela va REMPLACER les ${currentLignesCount} ligne(s) actuelle(s) de l'OR par celles du devis.\n\nContinuer ?`
      );
      if (!ok) return;
    }
    setImporting(true);
    try {
      const { data } = await api.post(`/or/${orId}/lignes/import-devis`, {
        devis_id: selectedId,
        mode: "replace",
      });
      toast.success(
        `Devis ${data.devis_numero || ""} importé · ${data.inserted} ligne(s)`
        + (data.deleted ? ` (a remplacé ${data.deleted})` : "")
      );
      onImported && onImported();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur d'import");
    } finally { setImporting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="import-devis-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-[#52a675]" /> Importer un devis dans l'OR
          </DialogTitle>
          <DialogDescription>
            Choisissez un devis du client. Ses lignes vont <b>remplacer</b> les lignes actuelles de l'OR.
            Le devis reste intact côté commercial.
          </DialogDescription>
        </DialogHeader>

        {currentLignesCount > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded p-3 text-sm flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
            <div>
              L'OR contient déjà <b>{currentLignesCount}</b> ligne(s). Elles seront <b>supprimées</b> et remplacées
              par les lignes du devis sélectionné.
            </div>
          </div>
        )}

        <div className="max-h-[50vh] overflow-y-auto -mx-6 px-6">
          {loading ? (
            <div className="text-center py-8 text-gray-400"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>
          ) : items.length === 0 ? (
            <div className="text-center py-8 text-sm text-gray-400">
              Aucun devis disponible pour ce client.
            </div>
          ) : (
            <div className="space-y-2">
              {items.map((d) => {
                const same = d.vehicule_id === vehiculeId;
                const statut = d.cycle || d.statut;
                return (
                  <button
                    key={d.id}
                    type="button"
                    onClick={() => setSelectedId(d.id)}
                    className={`w-full text-left p-3 rounded border transition ${
                      selectedId === d.id
                        ? "border-[#52a675] bg-[#52a675]/10 ring-2 ring-[#52a675]/30"
                        : "border-gray-200 hover:border-[#52a675]/40 hover:bg-gray-50"
                    }`}
                    data-testid={`import-devis-row-${d.id}`}
                  >
                    <div className="flex items-center gap-2 flex-wrap">
                      <code className="text-xs font-bold font-mono text-gray-700 bg-gray-50 px-1.5 py-0.5 rounded">
                        {d.numero}
                      </code>
                      <span className="text-xs text-gray-500">{STATUT_LABELS[statut] || statut}</span>
                      {same && (
                        <span className="text-[10px] bg-emerald-100 text-emerald-800 px-1.5 py-0.5 rounded">
                          Même véhicule
                        </span>
                      )}
                      <span className="text-xs text-gray-700 ml-2">{d.vehicule_label || ""}</span>
                      <span className="ml-auto font-mono font-semibold text-gray-900">
                        {fmtCHF(d.total_ttc)} CHF
                      </span>
                    </div>
                    {d.titre && (
                      <div className="text-xs text-gray-500 mt-1 truncate">{d.titre}</div>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button
            onClick={handleImport}
            disabled={!selectedId || importing}
            className="bg-[#52a675] hover:bg-[#3d8a5c]"
            data-testid="import-devis-confirm"
          >
            {importing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
            Remplacer les lignes par ce devis
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
